/**
 * Min Textilstudio, the logged-in hub that gathers saved combinations
 * and favorites (furniture + fabrics) and starts the configurator from anything.
 *
 * Markup: snippets/bf-textilstudio-page.liquid (rendered in the konto page)
 * Auth: window.bfUpAuth (bf-upholstery.js) shares the bearer token in localStorage.
 *
 * Backend status: /api/favorites and /api/boards exist. /api/saved-combinations does
 * not exist yet, so combinations are read/written API-first with a localStorage fallback
 * (bf_saved_combinations) until the endpoint is built. See Build Spec.
 */

import { createDebugger } from '~/scripts/utils/debug.js';
import { t } from '~/studio/lib/i18n.js';
import { sized } from '../utils/img.js';
// The configurator is a Vue app in the separate studio.bundle.js (loaded only on the konto
// page). It registers window.bfStudio.mountConfigurator, which the overlay calls below.

const debug = createDebugger('bf-textilstudio', false);

const COMBOS_KEY = 'bf_saved_combinations';

// ---- backend ----
function backendUrl() {
    return (
        (document.querySelector('meta[name="bf-backend-url"]') || {}).content ||
        'https://backend-sam-8064s-projects.vercel.app'
    );
}

function doFetch(url, opts) {
    return window.bfUpAuth
        ? window.bfUpAuth.authFetch(url, opts)
        : fetch(url, Object.assign({ credentials: 'include' }, opts));
}

async function apiGet(path) {
    const r = await doFetch(backendUrl() + path);
    if (!r.ok) throw Object.assign(new Error('HTTP ' + r.status), { status: r.status });
    return r.json();
}

// ---- analytics ----
function track(event, data) {
    try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(Object.assign({ event }, data || {}));
    } catch {}
    debug.log('track', event, data);
}

// ---- helpers ----
function escapeHtml(s) {
    return String(s == null ? '' : s).replace(
        /[&<>"']/g,
        (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
    );
}

function imgUrl(src) {
    if (!src) return '';
    if (src.startsWith('//')) return 'https:' + src;
    return src;
}

// Furniture vs fabric based on Shopify product.type (saved when favorited),
// not on handle guesses. A tyg/textil = fabric, everything else = furniture.
const TYPE_CACHE_KEY = 'bf_product_type_cache';

function typeIsFabric(productType) {
    return /tyg|textil|fabric/i.test(productType || '');
}

// Only Jio furniture can be reupholstered in the studio, so favorite furniture shows only those.
function isJioFurniture(f) {
    return /jio/i.test(f.product_vendor || '');
}

function readTypeCache() {
    try {
        return JSON.parse(localStorage.getItem(TYPE_CACHE_KEY) || '{}');
    } catch {
        return {};
    }
}

// Fill in missing product_type via Shopify's product JSON (cached per handle) so
// old favorites without a saved type are classified correctly until the backend backfills.
async function ensureTypes(items) {
    const cache = readTypeCache();
    let dirty = false;
    await Promise.all(
        items.map(async (it) => {
            if (it.product_type) return;
            const key = it.product_handle;
            if (!key) return;
            if (cache[key] != null) {
                it.product_type = cache[key];
                return;
            }
            try {
                const r = await fetch('/products/' + encodeURIComponent(key) + '.js');
                if (r.ok) {
                    const p = await r.json();
                    it.product_type = p.type || '';
                    cache[key] = it.product_type;
                    dirty = true;
                }
            } catch {}
        })
    );
    if (dirty) {
        try {
            localStorage.setItem(TYPE_CACHE_KEY, JSON.stringify(cache));
        } catch {}
    }
}

// ---- saved combinations: API-first med localStorage-fallback ----
function readLocalCombos() {
    try {
        return JSON.parse(localStorage.getItem(COMBOS_KEY) || '[]');
    } catch {
        return [];
    }
}

function writeLocalCombos(list) {
    try {
        localStorage.setItem(COMBOS_KEY, JSON.stringify(list));
    } catch {}
}

// Reupholsterable furniture = the textilstudio-mobler collection (rule: tag valj-tyg). Memoized set of
// product ids, used to HIDE saved combinations whose furniture isn't currently reupholsterable. Paginated
// because the storefront caps products.json at 250/page. Returns null if any page fails or the set is
// empty, so a failed/partial load never hides valid combos.
let _validFurniturePromise = null;
function validFurnitureIds() {
    if (!_validFurniturePromise) {
        _validFurniturePromise = (async () => {
            const ids = new Set();
            for (let page = 1; page <= 20; page++) {
                let batch;
                try {
                    const r = await fetch(
                        '/collections/textilstudio-mobler/products.json?limit=250&page=' + page,
                        { headers: { Accept: 'application/json' } }
                    );
                    if (!r.ok) return null;
                    const d = await r.json();
                    batch = d && Array.isArray(d.products) ? d.products : [];
                } catch {
                    return null;
                }
                batch.forEach((p) => ids.add(String(p.id)));
                if (batch.length < 250) break; // last page reached
            }
            return ids.size ? ids : null;
        })();
    }
    return _validFurniturePromise;
}

async function loadCombinations() {
    // Combinations live in localStorage. The backend has no /api/saved-combinations route (favorites and
    // boards do), so calling it only produced a 404 + CORS console error and always fell back here. Read
    // localStorage directly until that endpoint exists.
    let list = readLocalCombos();
    // Hide (do NOT delete) combos whose furniture isn't currently reupholsterable. A hard delete is
    // unsafe: a temporarily unpublished furniture is absent from the collection JSON just like a truly
    // removed one, and we can't tell them apart client-side, so deleting would risk permanent loss of a
    // customer's saved design. Hiding is reversible: the combo returns once the furniture is valid again.
    // The studio itself already guards a corrupt combo with a friendly error if one is opened directly.
    try {
        const valid = await validFurnitureIds();
        if (valid && valid.size) {
            const before = list.length;
            list = list.filter((c) => c.furnitureId != null && valid.has(String(c.furnitureId)));
            if (list.length !== before) debug.warn('hiding ' + (before - list.length) + ' non-reupholsterable combo(s)');
        }
    } catch (e) {
        debug.warn('combination filter skipped', e && e.message);
    }
    return list;
}

async function removeCombination(id) {
    // localStorage-only: no backend /api/saved-combinations route yet (see loadCombinations), so hitting
    // it would only 404 + throw a CORS error. Delete locally.
    writeLocalCombos(readLocalCombos().filter((c) => String(c.id) !== String(id)));
}

// ============================================================
// Rendering
// ============================================================
function money(amount) {
    // amount i kronor (heltal eller decimal). Enkel svensk formatering.
    if (amount == null || isNaN(amount)) return '';
    return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        maximumFractionDigits: 0
    }).format(amount);
}

function comboCard(c, configuratorUrl) {
    // Only the AI render fills the card cleanly. Older combos baked the furniture's white-background
    // product photo into previewImageUrl (== furnitureImage), so treat that as "no render" too and
    // show a neutral placeholder (the fabric swatch stays overlaid).
    const hasRender =
        c.previewImageUrl && c.previewImageUrl !== c.furnitureImage && c.previewImageUrl !== c.product_image;
    const preview = hasRender ? sized(imgUrl(c.previewImageUrl), 500) : '';
    const fabricSwatch = sized(imgUrl(c.fabricImage), 80);
    const href =
        configuratorUrl +
        '?furniture=' +
        encodeURIComponent(c.furnitureId) +
        '&fabric=' +
        encodeURIComponent(c.fabricId) +
        (c.fabricHandle ? '&fabric_handle=' + encodeURIComponent(c.fabricHandle) : '');
    const price = c.combinedPrice != null ? money(c.combinedPrice) : '';
    // The whole card opens the studio; no button. The remove-heart is guarded in the click handler.
    return `
      <article class="bf-ts-card bf-ts-card--combo" data-combo-id="${escapeHtml(c.id)}"
        data-open-studio role="button" tabindex="0"
        data-track="combination_open"
        data-href="${escapeHtml(href)}"
        data-furniture="${escapeHtml(c.furnitureId)}"
        data-fabric="${escapeHtml(c.fabricId)}"
        data-fabric-handle="${escapeHtml(c.fabricHandle || '')}"
        data-fabric-name="${escapeHtml(c.fabricName || '')}"
        data-fabric-image="${escapeHtml(c.fabricImage || '')}"
        data-studio-title="${escapeHtml(c.furnitureName || '')}"
        aria-label="${escapeHtml(t('hub.open_combination', { title: c.furnitureName || '' }))}">
        <div class="bf-ts-card__media">
          ${preview ? `<img src="${escapeHtml(preview)}" alt="${escapeHtml(t('hub.furniture_in_fabric', { furniture: c.furnitureName || '', fabric: c.fabricName || '' }))}" loading="lazy">` : '<div class="bf-ts-card__ph"></div>'}
          ${fabricSwatch ? `<span class="bf-ts-swatch" style="background-image:url(${escapeHtml(fabricSwatch)})" title="${escapeHtml(c.fabricName || '')}"></span>` : ''}
          <button type="button" class="bf-ts-heart is-on" data-remove-combo="${escapeHtml(c.id)}" aria-pressed="true" aria-label="${escapeHtml(t('hub.remove_combination'))}">${heartSvg()}</button>
        </div>
        <div class="bf-ts-card__body">
          <h3 class="bf-ts-card__title">${escapeHtml(c.furnitureName || '')}</h3>
          <p class="bf-ts-card__sub">i ${escapeHtml(c.fabricName || '')}</p>
          ${price ? `<p class="bf-ts-card__price">${escapeHtml(price)}</p>` : ''}
        </div>
      </article>`;
}

function favFurnitureCard(f, configuratorUrl) {
    const href = configuratorUrl + '?furniture=' + encodeURIComponent(f.product_id);
    return `
      <article class="bf-ts-card bf-ts-card--fit" data-fav-id="${escapeHtml(f.product_id)}">
        <div class="bf-ts-card__media">
          ${f.product_image ? `<img src="${escapeHtml(sized(imgUrl(f.product_image), 400))}" alt="${escapeHtml(f.product_title || '')}" loading="lazy">` : '<div class="bf-ts-card__ph"></div>'}
          <button type="button" class="bf-ts-heart is-on" data-remove-fav="${escapeHtml(f.product_id)}" aria-pressed="true" aria-label="${escapeHtml(t('hub.remove_favorite'))}">${heartSvg()}</button>
        </div>
        <div class="bf-ts-card__body">
          <h3 class="bf-ts-card__title">${escapeHtml(f.product_title || '')}</h3>
          <a class="bf-ts-btn bf-ts-btn--ghost bf-ts-btn--block" href="${escapeHtml(href)}" data-track="favorite_try_fabric" data-open-studio data-furniture="${escapeHtml(f.product_id)}" data-studio-title="${escapeHtml(f.product_title || '')}">Prova tyg</a>
        </div>
      </article>`;
}

function favFabricCard(f) {
    const href = '/products/' + encodeURIComponent(f.product_handle || '');
    return `
      <article class="bf-ts-card bf-ts-fabric" data-fav-id="${escapeHtml(f.product_id)}">
        <a class="bf-ts-card__media" href="${escapeHtml(href)}">
          ${f.product_image ? `<img src="${escapeHtml(sized(imgUrl(f.product_image), 400))}" alt="${escapeHtml(f.product_title || '')}" loading="lazy">` : '<div class="bf-ts-card__ph"></div>'}
        </a>
        <button type="button" class="bf-ts-heart is-on" data-remove-fav="${escapeHtml(f.product_id)}" aria-pressed="true" aria-label="${escapeHtml(t('hub.remove_favorite'))}">${heartSvg()}</button>
        <div class="bf-ts-card__body">
          <h3 class="bf-ts-card__title">${escapeHtml(f.product_title || '')}</h3>
        </div>
      </article>`;
}

function heartSvg() {
    return '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 21s-7.5-4.6-10-9.2C.6 9 1.7 5.7 4.8 5.1 6.9 4.7 8.8 6 12 8.6 15.2 6 17.1 4.7 19.2 5.1c3.1.6 4.2 3.9 2.8 6.7C19.5 16.4 12 21 12 21Z"/></svg>';
}

// Show the right sub-view in a section: grid | empty | loading | error
function setSectionState(section, state) {
    section.querySelectorAll('[data-studio-grid],[data-studio-empty],[data-studio-loading],[data-studio-error]').forEach(
        (n) => {
            const key = n.hasAttribute('data-studio-grid')
                ? 'grid'
                : n.hasAttribute('data-studio-empty')
                  ? 'empty'
                  : n.hasAttribute('data-studio-loading')
                    ? 'loading'
                    : 'error';
            n.hidden = key !== state;
        }
    );
}

function renderCount(section, n) {
    const el = section.querySelector('[data-studio-count]');
    if (el) el.textContent = n ? '(' + n + ')' : '';
}

// ============================================================
// Sektionsladdare
// ============================================================
function makeSection(root, name, loader, renderer, opts = {}) {
    const section = root.querySelector(`[data-studio-section="${name}"]`);
    if (!section) return null;
    const grid = section.querySelector('[data-studio-grid]');

    async function run() {
        // hideWhenEmpty: keep the whole section hidden during loading and if it is empty.
        if (!opts.hideWhenEmpty) setSectionState(section, 'loading');
        try {
            const items = await loader();
            renderCount(section, items.length);
            if (!items.length) {
                if (opts.hideWhenEmpty) section.hidden = true;
                else setSectionState(section, 'empty');
                return items;
            }
            section.hidden = false;
            grid.innerHTML = renderer(items);
            setSectionState(section, 'grid');
            return items;
        } catch (e) {
            debug.error('section ' + name + ' failed', e);
            if (opts.hideWhenEmpty) section.hidden = true;
            else setSectionState(section, 'error');
            return [];
        }
    }

    const retry = section.querySelector('[data-studio-retry]');
    if (retry) retry.addEventListener('click', run);

    return { section, grid, run };
}

// ---- full-screen overlay (configurator UI mounted separately) ----
function studioOverlay() {
    const overlay = document.querySelector('[data-studio-overlay]');
    if (!overlay) return { open() {}, close() {} };
    const titleEl = overlay.querySelector('[data-studio-overlay-title]');
    const contentEl = overlay.querySelector('[data-studio-overlay-content]');
    const closeBtn = overlay.querySelector('[data-studio-overlay-close]');

    // Shareable link: mirrors the open state + selection in the URL (?studio=1&furniture=&fabric=&board=)
    // so the configurator can be shared as a link and the back button closes it.
    function syncUrl(params, replace) {
        try {
            const url = new URL(window.location.href);
            url.searchParams.set('studio', '1');
            ['furniture', 'fabric', 'board'].forEach((k) => {
                if (params[k]) url.searchParams.set(k, params[k]);
                else url.searchParams.delete(k);
            });
            // Fabric handle rides along so a reload/shared link can rebuild the exact fabric.
            if (params.fabricHandle) url.searchParams.set('fabric_handle', params.fabricHandle);
            else url.searchParams.delete('fabric_handle');
            // Opening = new history entry (pushState). Selections inside the studio = replaceState,
            // so the back button closes instead of stepping through every fabric change.
            window.history[replace ? 'replaceState' : 'pushState']({ bfStudio: true }, '', url);
        } catch {}
    }
    function clearUrl() {
        try {
            const url = new URL(window.location.href);
            ['studio', 'furniture', 'fabric', 'board', 'fabric_handle'].forEach((k) => url.searchParams.delete(k));
            window.history.pushState({}, '', url);
        } catch {}
    }

    // The configurator is a Vue app in studio.bundle.js. It registers
    // window.bfStudio.mountConfigurator; wait for it if the bundle is still loading.
    function mountStudio(host, params, opts) {
        return new Promise((resolve, reject) => {
            const go = () => {
                try {
                    resolve(window.bfStudio.mountConfigurator(host, params, opts));
                } catch (e) {
                    reject(e);
                }
            };
            if (window.bfStudio && window.bfStudio.mountConfigurator) return go();
            let settled = false;
            const onReady = () => {
                if (settled) return;
                settled = true;
                document.removeEventListener('bf-studio:ready', onReady);
                go();
            };
            document.addEventListener('bf-studio:ready', onReady);
            window.setTimeout(() => {
                if (settled) return;
                settled = true;
                document.removeEventListener('bf-studio:ready', onReady);
                if (window.bfStudio && window.bfStudio.mountConfigurator) go();
                else reject(new Error('studio bundle not loaded'));
            }, 8000);
        });
    }

    let mounted = null;
    function unmount() {
        if (mounted) {
            mounted.unmount();
            mounted = null;
        }
    }

    // iOS Safari ignores overflow:hidden on html for touch scrolling, so the page behind
    // the overlay could still scroll and end up in a stuck, mis-hit state. position:fixed
    // on body is the only lock iOS honours; remember the scroll offset and restore it.
    let lockedScrollY = 0;
    function lockScroll() {
        lockedScrollY = window.scrollY || window.pageYOffset || 0;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = -lockedScrollY + 'px';
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
    }
    function unlockScroll() {
        document.documentElement.style.overflow = '';
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';
        window.scrollTo(0, lockedScrollY);
    }

    function close(opts) {
        overlay.hidden = true;
        unlockScroll();
        unmount();
        // Opened from a product PDP deep-link (?return=<product path>): go back to
        // that product instead of staying on the konto page, matching the wallpaper
        // visualizer. Only same-origin relative paths are honoured. Hub opens carry
        // no return param, so they just close in place.
        if (!(opts && opts.skipReturn)) {
            var returnUrl = '';
            try {
                returnUrl = new URLSearchParams(window.location.search).get('return') || '';
            } catch (e) {
                returnUrl = '';
            }
            if (returnUrl && returnUrl.charAt(0) === '/' && returnUrl.charAt(1) !== '/') {
                window.location.href = returnUrl;
                return;
            }
        }
        if (!(opts && opts.skipUrl)) clearUrl();
    }
    function open(params, opts) {
        // The bar title is the brand ("Brittfurn Textilstudio", set in Liquid),
        // not the furniture name. The selected furniture shows in the right panel instead.
        overlay.hidden = false;
        lockScroll();
        if (closeBtn) closeBtn.focus();
        unmount();
        if (contentEl) {
            contentEl.innerHTML = '<div class="bf-ts-overlay__placeholder">' + escapeHtml(t('hub.loading')) + '</div>';
            mountStudio(contentEl, params, {
                // Keep the URL in sync with the selection so the link always mirrors furniture + fabric.
                onChange: (sel) => syncUrl(sel, true)
            })
                .then((handle) => {
                    mounted = handle;
                })
                .catch((e) => {
                    debug.error('configurator could not load', e);
                    contentEl.innerHTML =
                        '<div class="bf-ts-overlay__placeholder">' + escapeHtml(t('hub.configurator_error')) + '</div>';
                });
        }
        if (!(opts && opts.skipUrl)) syncUrl(params);
    }

    function paramsFromUrl() {
        const sp = new URLSearchParams(window.location.search);
        return {
            furniture: sp.get('furniture') || '',
            fabric: sp.get('fabric') || '',
            fabricHandle: sp.get('fabric_handle') || '',
            board: sp.get('board') || '',
            // Satt av varukorgsraden: öppna kombinationen för redigering i stället
            // för att lägga till en ny (snippets/cart-item.liquid).
            edit: sp.get('edit') || '',
            // Känd rendering från varukorgsraden, så studion slipper beställa en ny.
            render: sp.get('render') || '',
            title: 'Textilstudio',
            _open: sp.get('studio') === '1'
        };
    }

    if (closeBtn) closeBtn.addEventListener('click', () => close());
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !overlay.hidden) close();
    });
    window.addEventListener('popstate', () => {
        const p = paramsFromUrl();
        if (p._open) open(p, { skipUrl: true });
        else if (!overlay.hidden) close({ skipUrl: true, skipReturn: true });
    });

    return { open, close, paramsFromUrl };
}

// ============================================================
// Init
// ============================================================
function init() {
    const root = document.querySelector('[data-bf-studio]');
    if (!root) return;

    const configuratorUrl = root.dataset.configuratorUrl || '/pages/textilstudio-konfigurator';
    const loggedIn = root.dataset.studioCustomerId ? true : false;

    // "Starta ny visualisering" everywhere, href as fallback (overlay opens via JS)
    root.querySelectorAll('[data-start-new]').forEach((a) => {
        if (a.tagName === 'A') a.setAttribute('href', configuratorUrl);
    });

    // Click tracking on deep-links (delegation). Skip control clicks (e.g. the remove-heart on a
    // combo card) so deleting a combo does not also fire combination_open.
    root.addEventListener('click', (e) => {
        if (e.target.closest('[data-remove-combo],[data-remove-fav]')) return;
        const t = e.target.closest('[data-track]');
        if (t) track(t.getAttribute('data-track'));
    });

    // Full-screen overlay: every configurator entry point opens it instead of navigating
    const overlay = studioOverlay();
    function openFromEl(opener) {
        if (opener.hasAttribute('data-start-new')) track('start_new_visualization');
        overlay.open({
            furniture: opener.getAttribute('data-furniture') || '',
            fabric: opener.getAttribute('data-fabric') || '',
            fabricHandle: opener.getAttribute('data-fabric-handle') || '',
            fabricName: opener.getAttribute('data-fabric-name') || '',
            fabricImage: opener.getAttribute('data-fabric-image') || '',
            board: opener.getAttribute('data-board') || '',
            title: opener.getAttribute('data-studio-title') || 'Textilstudio'
        });
    }
    root.addEventListener('click', (e) => {
        // A click on the remove-heart (or another control) must not open the studio.
        if (e.target.closest('[data-remove-combo],[data-remove-fav]')) return;
        const opener = e.target.closest('[data-open-studio]');
        if (!opener) return;
        e.preventDefault();
        openFromEl(opener);
    });
    // Keyboard: Enter/Space on a card acting as a button (saved combos).
    root.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        const opener = e.target.closest('[data-open-studio][role="button"]');
        if (!opener || opener !== e.target) return;
        e.preventDefault();
        openFromEl(opener);
    });

    // Shareable link: open the configurator directly if the URL points into the studio.
    const urlParams = overlay.paramsFromUrl();
    if (urlParams._open) overlay.open(urlParams, { skipUrl: true });

    // Guest: show banner, run only localStorage combinations. Favorites/boards require login.
    const combos = makeSection(
        root,
        'combinations',
        loadCombinations,
        (items) => items.map((c) => comboCard(c, configuratorUrl)).join(''),
        { hideWhenEmpty: true }
    );
    // The studio (same page, behind the overlay) writes combos to localStorage and dispatches this on
    // every save/remove. Re-run the section so a newly saved combination appears without a reload.
    document.addEventListener('bf:combos-changed', () => {
        if (combos) combos.run();
    });

    let favFurniture = null;
    let favFabric = null;
    // Favorites are fetched once and shared between the two sections.
    const favSectionFurniture = root.querySelector('[data-studio-section="fav-furniture"]');
    const favSectionFabric = root.querySelector('[data-studio-section="fav-fabric"]');

    async function loadFavorites() {
        if (!loggedIn) {
            [favSectionFurniture, favSectionFabric].forEach((s) => s && setSectionState(s, 'empty'));
            return;
        }
        [favSectionFurniture, favSectionFabric].forEach((s) => s && setSectionState(s, 'loading'));
        try {
            const { favorites } = await apiGet('/api/favorites');
            await ensureTypes(favorites || []);
            const furniture = (favorites || []).filter(
                (f) => !typeIsFabric(f.product_type) && isJioFurniture(f)
            );
            const fabric = (favorites || []).filter((f) => typeIsFabric(f.product_type));
            paintFav(favSectionFurniture, furniture, (f) => favFurnitureCard(f, configuratorUrl));
            paintFav(favSectionFabric, fabric, favFabricCard);
        } catch (e) {
            debug.error('favorites failed', e);
            [favSectionFurniture, favSectionFabric].forEach((s) => s && setSectionState(s, 'error'));
        }
    }

    function paintFav(section, items, render) {
        if (!section) return;
        renderCount(section, items.length);
        if (!items.length) {
            setSectionState(section, 'empty');
            return;
        }
        section.querySelector('[data-studio-grid]').innerHTML = items.map(render).join('');
        setSectionState(section, 'grid');
    }

    [favSectionFurniture, favSectionFabric].forEach((s) => {
        const retry = s && s.querySelector('[data-studio-retry]');
        if (retry) retry.addEventListener('click', loadFavorites);
    });

    // Optimistisk borttagning + undo (favoriter och kombinationer)
    root.addEventListener('click', (e) => {
        const favBtn = e.target.closest('[data-remove-fav]');
        if (favBtn) {
            e.preventDefault();
            removeFavoriteOptimistic(favBtn.getAttribute('data-remove-fav'));
            return;
        }
        const comboBtn = e.target.closest('[data-remove-combo]');
        if (comboBtn) {
            e.preventDefault();
            removeComboOptimistic(comboBtn.getAttribute('data-remove-combo'));
        }
    });

    function animateOut(card, done) {
        if (!card) return done();
        card.classList.add('bf-ts-card--out');
        setTimeout(done, 200);
    }

    function removeFavoriteOptimistic(productId) {
        const cards = root.querySelectorAll(`[data-fav-id="${CSS.escape(String(productId))}"]`);
        const section = cards[0] && cards[0].closest('[data-studio-section]');
        cards.forEach((card) =>
            animateOut(card, () => {
                card.remove();
                reflectEmptyIfNeeded(section);
            })
        );
        track('favorite_remove', { product_id: productId });
        doFetch(backendUrl() + '/api/favorites?product_id=' + encodeURIComponent(productId), {
            method: 'DELETE'
        }).catch((err) => debug.error('favorite delete failed', err));
        showUndo(t('hub.removed'), () => loadFavorites());
    }

    function removeComboOptimistic(id) {
        const card = root.querySelector(`[data-combo-id="${CSS.escape(String(id))}"]`);
        const section = card && card.closest('[data-studio-section]');
        animateOut(card, () => {
            if (card) card.remove();
            reflectEmptyIfNeeded(section);
        });
        track('combination_remove', { id });
        removeCombination(id);
        showUndo(t('hub.removed'), () => combos && combos.run());
    }

    function reflectEmptyIfNeeded(section) {
        if (!section) return;
        const grid = section.querySelector('[data-studio-grid]');
        const realCards = grid ? grid.querySelectorAll('.bf-ts-card:not(.bf-ts-add)').length : 0;
        renderCount(section, realCards);
        if (!realCards) setSectionState(section, 'empty');
    }

    // Run the sections
    if (combos) combos.run();
    loadFavorites();

    track('studio_hub_view', {
        logged_in: loggedIn
    });
}

// ---- undo toast (not shared with bf-saved to keep the studio standalone) ----
let undoTimer = null;
function showUndo(msg, onUndo) {
    document.querySelectorAll('.bf-ts-toast').forEach((t) => t.remove());
    if (undoTimer) clearTimeout(undoTimer);
    const toast = document.createElement('div');
    toast.className = 'bf-ts-toast';
    toast.innerHTML = `<span>${escapeHtml(msg)}</span><button type="button">${escapeHtml(t('hub.undo'))}</button>`;
    toast.querySelector('button').addEventListener('click', () => {
        toast.remove();
        onUndo();
    });
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('bf-ts-toast--in'));
    undoTimer = setTimeout(() => {
        toast.classList.remove('bf-ts-toast--in');
        setTimeout(() => toast.remove(), 300);
    }, 6000);
}

if (document.querySelector('[data-bf-studio]')) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
    else init();
}
